import type { Drawing } from "@mnk/shared/models/drawing";
import type { DrawingImage } from "@mnk/shared/models/drawingImage";
import type { Project } from "@mnk/shared/models/project";
import { useMemo, useState } from "react";

interface TableData {
  project: Project;
  currentIndex: number;
  totalCount: number;
  drawing: Drawing;
  drawingImage: DrawingImage;
  officeName: string;
  position: Position;
}

interface Position {
  x: number;
  y: number;
}

interface Bounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface DragState {
  isDragging: boolean;
  startPosition: Position;
  currentPosition: Position;
}

// 情報テーブルの描画とインタラクションを管理するフック
export const useInformationTable = () => {
  const [dragState, setDragState] = useState<DragState | null>(null);

  // テーブル設定
  const config = useMemo(
    () => ({
      tableWidth: 438,
      leftColumnWidth: 125,
      rowHeight: 44,
      companyRowHeight: 66,
      fontSize: 18,
      padding: 8,
      lineHeight: 25,
      maxProjectNameWidth: 413, // プロジェクト名の最大幅（パディング考慮）
    }),
    [],
  );

  // テーブルの境界を計算（プロジェクト名の高さを動的に計算）
  const getInformationTableBounds = (position: Position, projectName?: string): Bounds => {
    // プロジェクト名が提供された場合は実際の高さを計算
    let projectNameHeight = config.rowHeight;
    if (projectName) {
      // 仮のキャンバスを使って実際の折り返し行数を計算
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.font = `500 ${config.fontSize}px sans-serif`;
        const lines = wrapText(ctx, projectName, config.maxProjectNameWidth);
        projectNameHeight = Math.max(config.rowHeight, lines.length * config.lineHeight + config.padding * 2);
      }
    }

    return {
      x: position.x,
      y: position.y - (projectNameHeight - config.rowHeight),
      width: config.tableWidth,
      height: projectNameHeight + config.rowHeight * 4 + config.companyRowHeight,
    };
  };

  // ドラッグ開始時の処理
  const handleDragStart = (startPosition: Position) => {
    setDragState({
      isDragging: true,
      startPosition,
      currentPosition: startPosition,
    });
  };

  // ドラッグ中の処理
  const handleDragMove = (currentPosition: Position) => {
    if (!dragState) return;

    const dx = currentPosition.x - dragState.startPosition.x;
    const dy = currentPosition.y - dragState.startPosition.y;

    setDragState({
      ...dragState,
      currentPosition: {
        x: dragState.currentPosition.x + dx,
        y: dragState.currentPosition.y + dy,
      },
      startPosition: currentPosition,
    });
  };

  // ドラッグ終了時の処理
  const handleDragEnd = () => {
    setDragState(null);
  };

  interface Props {
    ctx: CanvasRenderingContext2D;
    data: TableData;
  }

  // テキストを指定幅で折り返す関数（改行文字にも対応）
  const wrapText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] => {
    // まず改行文字で分割
    const paragraphs = text.split(/\r?\n/);
    const allLines: string[] = [];

    for (const paragraph of paragraphs) {
      if (paragraph === "") {
        // 空行の場合はそのまま追加
        allLines.push("");
        continue;
      }

      // 各段落を幅で折り返し
      const chars = paragraph.split("");
      let currentLine = "";

      for (const char of chars) {
        const testLine = currentLine + char;
        const metrics = ctx.measureText(testLine);

        if (metrics.width > maxWidth && currentLine !== "") {
          allLines.push(currentLine);
          currentLine = char;
        } else {
          currentLine = testLine;
        }
      }

      if (currentLine !== "") {
        allLines.push(currentLine);
      }
    }

    return allLines.length > 0 ? allLines : [""];
  };

  // テーブルを描画する関数
  const drawInformationTable = (props: Props): Promise<void> => {
    return new Promise((resolve) => {
      const { ctx, data } = props;
      const { project, currentIndex, totalCount, drawing, drawingImage, position } = data;
      const { x, y } = dragState?.currentPosition ?? position;
      const {
        tableWidth,
        leftColumnWidth,
        rowHeight,
        companyRowHeight,
        fontSize,
        padding,
        lineHeight,
        maxProjectNameWidth,
      } = config;

      // コンテキストの状態を保存
      ctx.save();

      // プロジェクト名の折り返し処理
      ctx.font = `500 ${fontSize}px sans-serif`;
      const projectNameLines = wrapText(ctx, project.name, maxProjectNameWidth);
      const projectNameHeight = Math.max(rowHeight, projectNameLines.length * lineHeight + padding * 2);

      // 実際の位置に移動（プロジェクト名の高さを考慮）
      ctx.translate(x, y - (projectNameHeight - rowHeight));

      // テーブル全体の背景（白）
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, tableWidth, projectNameHeight + rowHeight * 4 + companyRowHeight);

      // プロジェクト名行（colSpan=2）の背景
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, tableWidth, projectNameHeight);

      // テーブルの外枠と内部の線をまとめて描画
      ctx.strokeStyle = "#111111";
      ctx.lineWidth = 1;

      // 外枠を描画
      ctx.beginPath();
      ctx.strokeRect(0, 0, tableWidth, projectNameHeight + rowHeight * 4 + companyRowHeight);

      // すべての横線を描画
      ctx.beginPath();
      ctx.moveTo(0, projectNameHeight);
      ctx.lineTo(tableWidth, projectNameHeight);
      ctx.stroke();

      for (let i = 1; i <= 4; i++) {
        ctx.beginPath();
        ctx.moveTo(0, projectNameHeight + rowHeight * i);
        ctx.lineTo(tableWidth, projectNameHeight + rowHeight * i);
        ctx.stroke();
      }

      // 縦線を描画
      ctx.beginPath();
      ctx.moveTo(leftColumnWidth, projectNameHeight);
      ctx.lineTo(leftColumnWidth, projectNameHeight + rowHeight * 4 + companyRowHeight);
      ctx.stroke();

      // プロジェクト名を描画（複数行対応）
      ctx.font = `500 ${fontSize}px sans-serif`;
      ctx.fillStyle = "#111111";
      ctx.textAlign = "center";
      const startY = (projectNameHeight - projectNameLines.length * lineHeight) / 2 + lineHeight - 5;
      projectNameLines.forEach((line, index) => {
        ctx.fillText(line, tableWidth / 2, startY + index * lineHeight);
      });

      // 以降のテキスト設定
      ctx.textAlign = "left";
      ctx.font = `${fontSize}px sans-serif`;

      // 図面名行
      ctx.fillText("図面名", padding, projectNameHeight + rowHeight / 2 + 5);
      ctx.fillText("平面図", leftColumnWidth + padding, projectNameHeight + rowHeight / 2 + 5);

      // 区間又は場所行
      ctx.fillText("区間又は場所", padding, projectNameHeight + rowHeight + rowHeight / 2 + 5);
      ctx.fillText(drawing.name, leftColumnWidth + padding, projectNameHeight + rowHeight + rowHeight / 2 + 5);

      // 区間・IC名行
      const sectionLabel =
        drawingImage.startKiloPost === 10000000
          ? (drawingImage.sectionName ?? "")
          : `KP: ${drawingImage.startKiloPost.toFixed(2)} 〜 ${drawingImage.endKiloPost.toFixed(2)}`;
      ctx.fillText("区間・IC名", padding, projectNameHeight + rowHeight * 2 + rowHeight / 2 + 5);
      ctx.fillText(sectionLabel, leftColumnWidth + padding, projectNameHeight + rowHeight * 2 + rowHeight / 2 + 5);

      // 図面番号行
      ctx.fillText("図面番号", padding, projectNameHeight + rowHeight * 3 + rowHeight / 2 + 5);
      ctx.fillText(
        `${currentIndex + 1} / ${totalCount}`,
        leftColumnWidth + padding,
        projectNameHeight + rowHeight * 3 + rowHeight / 2 + 5,
      );

      // 事業所名行
      ctx.fillText("事業所名", padding, projectNameHeight + rowHeight * 4 + rowHeight / 2 + 5);

      // 会社名と場所を適切な位置に配置
      const firstLineY = projectNameHeight + rowHeight * 4 + lineHeight;
      ctx.fillText("(株)ネクスコ・メンテナンス関東", leftColumnWidth + padding, firstLineY);
      ctx.fillText(data.officeName, leftColumnWidth + padding, firstLineY + lineHeight);

      // コンテキストの状態を復元
      ctx.restore();

      // 描画が完了したことを通知
      resolve();
    });
  };

  return {
    drawInformationTable,
    getInformationTableBounds,
    tableConfig: config,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    isDragging: dragState?.isDragging ?? false,
    dragState,
  };
};
